<template>
  <div class="flex-1 min-h-0 flex flex-col bg-[#FCFCFD] overflow-hidden">
    <InviteBanner v-if="showInviteBanner" :file="file!" />

    <div
      class="flex-1 min-h-0 flex flex-col overflow-y-auto px-4 tablet-h:px-5 tablet-h:pt-10 tablet-h:pb-0"
      :class="
        userStore.user
          ? 'pt-[calc(env(safe-area-inset-top)+80px)] pb-[calc(env(safe-area-inset-bottom)+100px)]'
          : 'pt-[30px] pb-[calc(env(safe-area-inset-bottom)+16px)]'
      "
    >
      <CopyrightHeader :file="file" />

      <div class="flex flex-col tablet-h:flex-row tablet-h:justify-center">
        <!-- Left side container -->
        <div class="tablet-h:w-full tablet-h:max-w-[548px] tablet-h:mr-[18px]">
          <CopyrightMedia
            :file="file"
            :can-download="canDownload"
            :show-menu="showThreeDotsMenu"
            :has-privileged-role="hasPrivilegedRole"
            :is-private="isPrivate"
            @toggle-viewer="previewMode.toggle"
            @download="handleDownloadClick"
            @share="handleShareClick"
            @three-dots="handleThreeDotsClick"
          />
        </div>

        <!-- Right side container -->
        <div
          class="tablet-h:w-full tablet-h:max-w-[774px] text-[14px] overflow-x-hidden"
        >
          <CopyrightInfoCard :file="file" class="mb-4" />

          <AnalysisCard v-if="showAnalysis" :file="file" class="mb-4" />

          <CopyrightDescription
            :file="file"
            :is-owner="isOwner"
            :loading="loading"
            @edit="handleEditSettings"
          />
        </div>
      </div>

      <CopyrightFooter v-if="showFooter" />

      <div
        v-if="file"
        class="w-full max-w-[1340px] mx-auto mt-10 tablet-h:mt-5 tablet-h:border-t border-[#d4d8e1] tablet-h:pt-5 text-[12px] leading-[18px] text-gray-400 mb-[30px]"
      >
        This registration constitutes a record of authorship, ownership, and the
        date of registration, and serves as rights-administration information.
        Where a government jurisdiction is selected, this record is an official
        government copyright registration issued under the laws of that
        jurisdiction, entitling the rightsholders identified herein to the
        rights and protections provided thereunder.
      </div>
    </div>
  </div>

  <FileViewer
    v-if="previewMode.isOpen.value && file"
    :file="file"
    @close-viewer="previewMode.toggle"
  />
</template>

<script setup lang="ts">
import FileViewer from '@/features/explorer/components/FileViewer.vue'
import InviteBanner from '@/shared/components/InviteBanner.vue'
import CopyrightInfoCard from '@/features/copyright/components/CopyrightInfoCard.vue'
import AnalysisCard from '@/features/infringement/components/AnalysisCard.vue'
import CopyrightHeader from '@/features/copyright/components/CopyrightHeader.vue'
import CopyrightMedia from '@/features/copyright/components/CopyrightMedia.vue'
import CopyrightDescription from '@/features/copyright/components/CopyrightDescription.vue'
import CopyrightFooter from '@/features/copyright/components/CopyrightFooter.vue'
import { openSheet, Sheets } from '@/shared/dialogs/sheets'
import { openModal, Modals } from '@/shared/dialogs/modals'
import { openDialog, Dialog } from '@/shared/dialogs/dialog'
import { useHead } from '@unhead/vue'
import { computed, watch, watchEffect } from 'vue'
import { useDevice } from '@/shared/composables/useDevice'
import { useRoute, useRouter } from 'vue-router'
import { useUserStore } from '@/shared/stores/user'
import { isSignedIn } from '@dacr/auth'
import { useCopyrightFile } from '@/features/copyright/composables/useCopyrightFile'
import { usePreviewMode } from '@/shared/composables/usePreviewMode'

const route = useRoute()
const router = useRouter()
const device = useDevice()
const userStore = useUserStore()
const fileId = computed(() => route.params.id as string)

const {
  file,
  loading,
  error,
  isOwner,
  isCoOwner,
  isAuthor,
  hasPrivilegedRole,
  canDownload,
  isPrivate,
} = useCopyrightFile(fileId)

const previewMode = usePreviewMode()

const title = computed(() => previewMode.pageTitle(file.value?.name))
useHead({
  title,
  meta: [
    {
      name: 'description',
      content:
        'Permanent, official registration for every creative work, on the world’s sovereign copyright infrastructure.',
    },
  ],
})

watch(error, () => {
  if (error.value && route.name === 'CopyrightView') router.push('/404')
})

const showInviteBanner = computed(() => {
  if (!file.value) return false
  if (!userStore.isSignedIn) return !!route.query.email
  return !!file.value.notifications?.length
})

const showFooter = computed(() => !loading.value)
const showThreeDotsMenu = computed(
  () => isOwner.value || isCoOwner.value || isAuthor.value
)
const showAnalysis = computed(() => !!file.value)

// Handlers
const handleEditSettings = () => {
  openDialog(Dialog.FileSettings, {
    props: { file: file.value, focusDescription: true },
  })
}

const handleShareClick = () => {
  if (!device.isMobile.value) {
    return openDialog(Dialog.Share, {
      props: { file: file.value },
    })
  }

  if (isOwner.value || isCoOwner.value) {
    openDialog(Dialog.Share, {
      props: { file: file.value },
    })
  } else {
    navigator.share({
      title: `${file.value?.metadata.originalFilename} | Dacr`,
      url: `${window.location.origin}/file/${file.value?.copyright.id}`,
    })
  }
}

const handleDownloadClick = () => {
  openModal(Modals.Download, {
    props: { file: file.value },
  })
}

const handleThreeDotsClick = () => {
  openSheet(Sheets.FileActions, {
    props: { file: file.value },
  })
}

// Email invite flow
watchEffect(() => {
  if (!route.query.email || isSignedIn() || !file.value?.settings.isPrivate)
    return

  setTimeout(() => {
    openDialog(Dialog.FromInvite, {
      props: { email: route.query.email },
    })
  }, 3000)
})
</script>
